import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { Recipe } from '@/types';
import html2canvas from 'html2canvas';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(date: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }).format(date);
}

export function formatTime(minutes: number): string {
  if (!minutes || isNaN(minutes)) return '0 min';
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  if (hours === 0) {
    return `${minutes}m`;
  }
  
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
}

export async function generateRecipeCard(recipe: Recipe): Promise<Blob> {
  // Create a temporary container
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.width = '800px';
  container.style.padding = '32px';
  container.style.backgroundColor = 'white';
  container.style.fontFamily = 'Lato, system-ui, -apple-system, sans-serif';

  // Create recipe card HTML
  container.innerHTML = `
    <div style="max-width: 800px; background: white; border-radius: 12px; overflow: hidden;">
      ${recipe.imageUrl ? `
        <div style="width: 100%; height: 400px; overflow: hidden;">
          <img 
            src="${recipe.imageUrl}" 
            alt="${recipe.name}"
            style="width: 100%; height: 100%; object-fit: cover;"
            crossorigin="anonymous"
          />
        </div>
      ` : ''}
      <div style="padding: 32px;">
        <h2 style="font-size: 28px; font-weight: 600; color: #1a1a1a; margin: 0 0 16px 0;">
          ${recipe.name}
        </h2>
        
        <div style="display: flex; gap: 24px; margin-bottom: 32px;">
          <div style="font-size: 16px; color: #666;">
            Prep: ${formatTime(recipe.prepTime)} • Cook: ${formatTime(recipe.cookTime)}
          </div>
          <div style="font-size: 16px; color: #666;">
            ${recipe.servings} servings
          </div>
        </div>

        <div style="margin-bottom: 32px;">
          <h3 style="font-size: 20px; font-weight: 500; margin: 0 0 16px 0;">Ingredients</h3>
          <ul style="margin: 0; padding: 0 0 0 20px;">
            ${recipe.ingredients.map(i => `
              <li style="margin-bottom: 12px; color: #333; font-size: 16px;">
                ${i.amount} ${i.unit} ${i.name}
              </li>
            `).join('')}
          </ul>
        </div>

        <div>
          <h3 style="font-size: 20px; font-weight: 500; margin: 0 0 16px 0;">Instructions</h3>
          <ol style="margin: 0; padding: 0 0 0 20px;">
            ${recipe.instructions.map(instruction => `
              <li style="margin-bottom: 16px; color: #333; font-size: 16px;">
                ${instruction}
              </li>
            `).join('')}
          </ol>
        </div>

        ${recipe.tags?.length ? `
          <div style="margin-top: 32px;">
            ${recipe.tags.map(tag => `
              <span style="display: inline-block; padding: 6px 12px; background: #fff7ed; color: #ea580c; border-radius: 9999px; font-size: 14px; margin-right: 8px;">
                ${tag}
              </span>
            `).join('')}
          </div>
        ` : ''}
      </div>
    </div>
  `;

  document.body.appendChild(container);

  // Wait for images to load
  await Promise.all(
    Array.from(container.getElementsByTagName('img')).map(
      img => new Promise((resolve) => {
        if (img.complete) resolve(true);
        img.onload = () => resolve(true);
        img.onerror = () => resolve(true);
      })
    )
  );

  // Generate image using html2canvas
  const canvas = await html2canvas(container, {
    scale: 2,
    useCORS: true,
    allowTaint: true,
    backgroundColor: 'white',
    windowWidth: 800,
    width: 800
  });

  // Remove temporary container
  document.body.removeChild(container);

  // Convert canvas to blob
  return new Promise(resolve => {
    canvas.toBlob(blob => {
      resolve(blob as Blob);
    }, 'image/png', 1.0);
  });
}

export async function shareRecipe(recipe: Recipe): Promise<boolean> {
  try {
    const recipeImage = await generateRecipeCard(recipe);
    const file = new File([recipeImage], `${recipe.name.toLowerCase().replace(/\s+/g, '-')}-recipe.png`, {
      type: 'image/png'
    });

    if (navigator.share && navigator.canShare) {
      const shareData = {
        title: recipe.name,
        text: `Check out this recipe for ${recipe.name}!`,
        files: [file]
      };

      if (navigator.canShare(shareData)) {
        await navigator.share(shareData);
        return true;
      }
    }

    // Fallback to download if sharing not available
    const imageUrl = URL.createObjectURL(recipeImage);
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = `${recipe.name.toLowerCase().replace(/\s+/g, '-')}-recipe.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(imageUrl);
    return true;
  } catch (error) {
    console.error('Error sharing recipe:', error);
    return false;
  }
}